import { Component, EventEmitter, HostListener, Injector, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import DataGrid from 'devextreme/ui/data_grid';
import * as _moment from 'moment';
import { Subscription } from 'rxjs';

// components
import { FullListComponent } from '../../shared/components/full-list/full-list.component';
import { PrebidOnLotComponent } from '../shared/components/prebids/prebid-on-lot.component';

// models
import { ApplicationSettings } from '../../shared/models/application-settings';
import { AuctionCluster } from '../../shared/models/auction-cluster';
import { BuyerActionOnLotGroup, GenericLotColumn, GenericLotRow } from '../../shared/models/generic-lot';
import { Catalog, PrebidMinMaxAmountsEnum } from '../../shared/models/catalog';
import { ProductPropertyTypeEnum } from '../../shared/enum/productPropertyTypeEnum';
import { UrlTypeEnum } from '../../shared/enum/urlTypeEnum';

import { BuyerClock } from '../shared/models/buyer-level-clocks';
import { BuyerLevelSubbuyer } from '../shared/models/buyer-level-subbuyer';

// services
import { TranslateService } from '@ngx-translate/core';
import { ServiceFactory } from '../../shared/factory/service-factory';
import { TokenService } from '../../shared/services/token.service';
import { WebApiService } from '../../shared/services/web-api.service';

import { CatalogService } from '../shared/services/catalog.service';
import { SupplyMatrixService } from '../shared/services/supply-matrix.service';

const moment = (_moment as any).default ? (_moment as any).default : _moment;

@Component({
  selector: 'buyer-supply-matrix-lots-component',
  templateUrl: 'buyer-supply-matrix-lots.component.html',
  styleUrls: ['./buyer-supply-matrix-lots.component.scss']
})
export class BuyerSupplyMatrixLotsComponent extends FullListComponent<GenericLotRow, PrebidOnLotComponent> implements OnInit, OnDestroy {

  catalogService: CatalogService;

  auctionClusterId: number = null;
  auctionClusters: Array<AuctionCluster> = [];
  catalogId: number = null;
  catalogs: Array<Catalog> = [];
  clocks: Array<BuyerClock> = [];
  date: Date = new Date();
  columns: Array<any> = [];
  subbuyers: Array<BuyerLevelSubbuyer> = [];
  data: Array<any> = new Array<any>();
  objectKeys: Array<string> = new Array<string>();
  items: Array<GenericLotRow> = [];
  filterPrebids = false;
  filterMarked = false;
  lastRefresh = '';
  currentPage = 1;
  lastRecordedPage = 1;
  lotIds: Array<number> = [];
  isOpened = false;
  dataGridInstance: DataGrid;
  modalTitle: string;

  tokenService: TokenService;
    
  popUpShowed: boolean = false;
  popUpOpened: boolean = false;
  carrouselLoaded: boolean = false;
  carrouselData: Array<any> = [];
  carrouselShown: boolean = false;

  @ViewChild('prebid') buyerSupplyMatrixPrebidComponent: PrebidOnLotComponent;

  @Output() onClosed = new EventEmitter<any>(); // tslint:disable-line:no-output-on-prefix

  rtlEnabled = localStorage.getItem('last-selected-language-direction') ? JSON.parse(localStorage.getItem('last-selected-language-direction')) : false;
  private _subscription: Subscription;
  gridColumns = [];

  constructor(
    protected injector: Injector,
    protected appSettings: ApplicationSettings,
    protected route: ActivatedRoute,
    protected webApiService: WebApiService,
    private supplyMatrixService: SupplyMatrixService,
    private translateService: TranslateService,
  ) {
    super(injector, GenericLotRow);
    this.getServices(route, appSettings, webApiService);

    this.tokenService = this.injector.get(TokenService);
    this._subscription = this.language.direction.subscribe(dir => {
      this.rtlEnabled = dir;
    });
  }

  ngOnInit() {
    this.setTranslations('SUPPLY_MATRIX');
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this._subscription.unsubscribe();
  }

  getServices(route, appSettings, webApiService) {
    this.catalogService = ServiceFactory.getCatalogService(route, appSettings, webApiService) as CatalogService;
  }

  getData() {
    return;
  }

  open(auctionClusterId: number, catalogId: number, date: Date,
    lotIds: Array<number>, subbuyers: Array<BuyerLevelSubbuyer>) {
    this.auctionClusterId = auctionClusterId;
    this.catalogId = catalogId;
    this.date = date;
    this.lotIds = lotIds;
    this.subbuyers = subbuyers;
    this.isOpened = true;
    this.fetchLots(true);
    this.modalTitle = this.getTranslation(this.translations.PREBID);
  }

  onPrebidClosed() {
    this.fetchLots(false);
  }

  fetchLots(refreshColumns: boolean) {
    // Reset any existing filtering if column refresh is requested
    if (refreshColumns) {
      this.columns = [];
    }

    this.data = [];
    this.items = [];
    const dateFormatted = moment(this.date).local().format('YYYY-MM-DD');
    this.spinner.show();

    this.catalogService.getAllSupplyCatalogs()
      .subscribe(result => {
        this.catalogs = result;
      },
        error => {
          this.errorService.show(error);
        });

    this.supplyMatrixService.getLotsForPrebids(this.id, this.auctionClusterId, this.catalogId, dateFormatted, this.lotIds)
      .subscribe(result => {
        this.items = result.rows;
        if (refreshColumns) {
          // Parse columns only if explicitly requested
          this.parseColumns(result.columnTitles);
        }
        this.parseObject(result.rows);
        this.spinner.hide();
        this.lastRefresh = moment().locale(this.translateService.currentLang).format('DD.MM.YYYY. HH:mm');
        this.generateTable(result);
      },
        error => {
          this.errorService.show(error);
          this.spinner.hide();
        });
  }

  private parseObject(rows: Array<GenericLotRow>) {
    this.data = [];

    // Ignore parsing if there are no columns defined
    if (this.columns.length === 0) {
      return;
    }

    // Apply predib/marked filter for lots
    if (this.filterPrebids || this.filterMarked) {
      rows = rows.filter(_ => {
        return (this.filterPrebids && _.prebid) || (this.filterMarked && _.hasMark);
      });
    }

    rows.forEach(row => {
      const obj = new Object();
      this.columns.forEach((column, i) => {
        // Parse prebids
        if (row.prebid) {
          // Add prebid price in values
          if (row.prebid.price !== 0) {
            row.values.push(row.prebid.price.toFixed(2).toString());
          } else {
            row.values.push(row.prebid.buyerActionOnLot.map(_ => _.price).join(', '));
          }
          // Find and add subbuyer in values
          const subbuyer = this.subbuyers.find(_ => _.buyerId === row.prebid.subbuyerId);
          if (subbuyer) {
            row.values.push(`${subbuyer.subNumber} - ${subbuyer.name}`);
          } else {
            // Add null if there is no subbuyer
            row.values.push(null);
          }
        }
        if (column.propertyTypeId === ProductPropertyTypeEnum.DATE) {
          obj['key' + i] = this.addLineBreaks(row.values[i], column.propertyTypeId, column.propertyTypeFormatId);
        } else if (column.propertyTypeId === ProductPropertyTypeEnum.URL) {
          obj['key' + i] = row.values[i];
        } else if (column.propertyTypeId === ProductPropertyTypeEnum.TEXT
          || column.propertyTypeId === ProductPropertyTypeEnum.BOOLEAN
          || column.propertyTypeId === ProductPropertyTypeEnum.IMAGE
          || column.propertyTypeId === ProductPropertyTypeEnum.MASTER_DATA
          || isNaN(Number(row.values[i]))) {
          // Add line breaks for string value
          let objValue = this.getTranslation(row.values[i]);
          objValue = this.addLineBreaks(objValue, column.propertyTypeId);
          obj['key' + i] = objValue;
        } else {
          obj['key' + i] = this.format(Number(row.values[i]), column.propertyTypeId);
        }
      });
      obj['__item__'] = row;

      this.data.push(obj);
    });

    // Reset default page size
    this.currentPage = this.lastRecordedPage;
    this.setPageSize(this.pageSizeValue);
  }

  private parseColumns(columns: Array<GenericLotColumn>) {
    const _columns = [];
    columns.forEach(column => {
      _columns.push({
        name: this.getTranslation(column.name),
        propertyTypeId: column.propertyTypeId,
        propertyTypeFormatId: column.propertyTypeFormatId
      });
    });

    // Additional columns for displaying prebid info
    _columns.push({
      name: this.getTranslation(this.translations.PREBID_PRICE),
      propertyTypeId: null
    });
    _columns.push({
      name: this.getTranslation(this.translations.PREBID_SUBBUYER),
      propertyTypeId: null
    });


    const _objectKeys = [];
    _columns.forEach((column, i) => {
      _objectKeys.push('key' + i);
    });

    this.objectKeys = _objectKeys;
    this.columns = _columns;
    this.createDataSchema();
  }

  findLot(lotId: number) {
    for (let i = 0; i < this.data.length; i += 1) {
      if (this.data[i].__item__ && this.data[i].__item__.id === lotId) {
        return this.data[i].__item__;
      }
    }

    return null;
  }

  edit(lotId: number, event: Event) {
    event.stopPropagation();

    this.buyerSupplyMatrixPrebidComponent.modalTitle = this.translations.PREBID;
    const lot = this.findLot(lotId);
    lot.lotIds = lot.groupedIds;
    const catalog = this.catalogs.find(c => c.catalogId === this.catalogId);
    var showMaxAmountPrebidField = catalog.prebidMinMaxAmounts !== PrebidMinMaxAmountsEnum.PREBIDS_ARE_IGNORED; 
    
    this.buyerSupplyMatrixPrebidComponent.open(this.items, lotId, this.auctionClusterId, this.catalogId, this.subbuyers,
      this.date, catalog.allowPartialSaleOfSingleLotWithMultipleSaleUnits, catalog.amountIsBoxContentMultitude, catalog.allowMultiplePrebidsPerBuyerOnSameLot,
      showMaxAmountPrebidField);
  }

  markLot(lot: GenericLotRow, isMark: boolean, event: Event) {
    event.stopPropagation();
    const prebid = new BuyerActionOnLotGroup();
    prebid.buyerId = this.id;
    prebid.catalogId = this.catalogId;
    prebid.groupedIds = lot.groupedIds;

    if (isMark) {
      this.supplyMatrixService.saveMark(this.id, prebid, this.auctionClusterId).subscribe(result => {
        this.fetchLots(false);
      }, err => {
        this.errorService.show(err);
      });
    } else {
      this.supplyMatrixService.deleteMark(this.id, prebid, this.auctionClusterId).subscribe(result => {
        this.fetchLots(false);
      }, err => {
        this.errorService.show(err);
      });
    }
  }

  deleteSelected() {
    //
  }

  onClose() {
    this.isOpened = false;
    this.onClosed.emit();
  }

  initColumn = (e: any) => {
    this.dataGridInstance = e.component;
  }

  editBackground(e: any) {
    if (e.rowType == 'data') {
      if (e.data.__item__.prebid) {
        e.rowElement.style.backgroundColor = '#A3EDB2';
      }
      else if (e.data.__item__.hasMark) {
        e.rowElement.style.backgroundColor = '#A3C4ED';
      }
    }
  }

  editCellBackground(e: any) {
    if (e.rowType == 'data') {
      if (e.data.__item__.prebid) {
        e.cellElement.style.backgroundColor = '#A3EDB2';
      }
      else if (e.data.__item__.hasMark) {
        e.cellElement.style.backgroundColor = '#A3C4ED';
      }
    }
  }

  generateTable = (rows: any) => {
    this.gridColumns = [];
    let gridCurrentColums = this.dataGridInstance.getVisibleColumns();
    rows.columnTitles.forEach((row: any, i: number) => {
      let columnName = this.getTranslation(row.name);
      if (this.gridColumns.includes(columnName)) {
        let j = 0;
        do {
          columnName = this.getTranslation(row.name) + j;
          j++;
        } while (this.gridColumns.includes(columnName));
      }
      this.gridColumns.push(columnName);
      const isColumn = gridCurrentColums.find(c => c.dataField === columnName);
      const isImage = (row.propertyTypeId === ProductPropertyTypeEnum.URL &&
        row.propertyTypeFormatId === UrlTypeEnum.IMAGE_URL);

      if (!isColumn) {
        this.dataGridInstance.addColumn({
          dataField: columnName,
          caption: this.getTranslation(row.name),
          visibleIndex: i,
          allowEditing: false,
          encodeHtml: false,
          cellTemplate: isImage ? 'imageCellTemplate' : 'cellTemplate'
        });
      }
    });

    const isColumnPrebidPrice = gridCurrentColums.find(c => c.dataField === this.getTranslation(this.translations.PREBID_PRICE));
    this.gridColumns.push(this.getTranslation(this.translations.PREBID_PRICE));
    if (!isColumnPrebidPrice) {
      gridCurrentColums = this.dataGridInstance.getVisibleColumns();
      this.dataGridInstance.addColumn({
        dataField: this.getTranslation(this.translations.PREBID_PRICE),
        caption: this.getTranslation(this.translations.PREBID_PRICE),
        visibleIndex: gridCurrentColums.length - 1,
        allowEditing: false
      });

    }

    const isColumnPrebidSubbuyer = gridCurrentColums.find(c => c.dataField === this.getTranslation(this.translations.PREBID_SUBBUYER));
    this.gridColumns.push(this.getTranslation(this.translations.PREBID_SUBBUYER));
    if (!isColumnPrebidSubbuyer) {
      gridCurrentColums = this.dataGridInstance.getVisibleColumns();
      this.dataGridInstance.addColumn({
        dataField: this.getTranslation(this.translations.PREBID_SUBBUYER),
        caption: this.getTranslation(this.translations.PREBID_SUBBUYER),
        visibleIndex: gridCurrentColums.length - 1,
        allowEditing: false
      });

    }

    this.generateTableData();
  }


  generateTableData() {
    const gridItems = [];
    this.data.forEach((row, i) => {
      let gridItem = {};
      var imageColumns =  this.columns.filter(tableColumn => tableColumn.propertyTypeId === ProductPropertyTypeEnum.URL && tableColumn.propertyTypeFormatId === UrlTypeEnum.IMAGE_URL);

      this.gridColumns.forEach((column, j) => {
        if (imageColumns.length !== 0 && imageColumns.filter(_ => _.name === column).length !== 0) {
          gridItem[column] = row['key' + j];
        } else {
          gridItem[column] = this.getTranslation(row['key' + j]);
        }
      });

      gridItem['__item__'] = row.__item__;

      gridItems.push(gridItem);
    });

    const rows = this.dataGridInstance.getDataSource();
    if (rows === null) {
      if (gridItems.length > 0) {
        this.dataGridInstance.option('dataSource', gridItems);
      }
    } else {
      this.dataGridInstance.option('dataSource', gridItems);
    }
  }

  doesImageCalculationExist(data: any) {
    if (data && JSON.parse(data).find(el => el.field_0 !== '')) {
      return true;
    }
    return false;
  }

  getImage(data: any) {
    let imageUrl = JSON.parse(data).find(el => el.field_0 !== '').field_0;
    return imageUrl + '-thumbnail';
  }

  showImageCarrousel(data: any) {
    this.carrouselData = JSON.parse(data).filter(el => el.field_0 !== '');
    this.spinner.show();
    this.popUpOpened = true;

    if (this.carrouselLoaded) {
      this.spinner.hide();
      this.popUpShowed = true;
      this.carrouselShown = true;
    }
  }

  openPopUp() {
    this.spinner.hide();
    this.popUpShowed = true;
    this.carrouselLoaded = true;
    this.carrouselShown = true;
  }

  closePopUp() {
    this.popUpOpened = false;
    this.popUpShowed = false;
    this.carrouselLoaded = false;
  }

  @HostListener('window:keydown', ['$event']) onKeydownHandler(event: KeyboardEvent) {
    if (event.key === "Escape") {
      if(this.buyerSupplyMatrixPrebidComponent.isOpened || this.carrouselShown) {
        this.carrouselShown = false;
        return;
      } else {
        event.stopPropagation();
        this.isOpened = false;
      }
    }
  }

  add() { }
}
